"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.composeThemeManifest = composeThemeManifest;
exports.readThemePackage = readThemePackage;
function record(value) {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value
        : null;
}
function positiveInt(value) {
    return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}
/**
 * 变体补 `key`。
 *
 * 工坊那边的 `variants.json` 用对象键当标识（`{ "wide": { label, tokens } }`），
 * 而运行时要求每个变体自己带 `key` —— 渲染层与保存校验都读 `variant.key`，
 * 缺了它，控制台的下拉框会是一排空值。
 */
function normalizeVariants(raw) {
    if (!raw)
        return {};
    const out = {};
    for (const [key, value] of Object.entries(raw)) {
        const variant = record(value);
        if (!variant)
            continue;
        const tokens = record(variant['tokens']);
        out[key] = {
            key,
            label: (record(variant['label']) ?? {}),
            tokens: (tokens ?? {}),
        };
    }
    return out;
}
function composeThemeManifest(input) {
    const meta = record(input.meta);
    const tokens = record(input.tokens);
    const variants = record(input.variants);
    const monthlyCents = positiveInt(meta?.['priceMonthlyCents']);
    const annualCents = positiveInt(meta?.['priceAnnualCents']);
    /**
     * 已经是完整 manifest 的情形（上传了一份 manifest.json 而不是工坊的三份）：
     * 价格在 `meta.pricing` 里，不在 `priceMonthlyCents`。两种都要认，
     * 否则"上传完整 manifest"会把定价丢掉 —— 而丢定价等于把一套付费主题
     * 变成免费主题，市场页上显示 ¥0，没人会发现这是解析问题。
     */
    const pricingNode = record(meta?.['pricing']);
    const pricingMonthly = positiveInt(pricingNode?.['monthlyCents']) ?? monthlyCents;
    const pricingAnnual = positiveInt(pricingNode?.['annualCents']) ?? annualCents;
    const manifest = {
        name: String(meta?.['slug'] ?? meta?.['name'] ?? ''),
        version: String(meta?.['version'] ?? '1.0.0'),
        tokens: {
            primitive: (record(tokens?.['primitive']) ?? {}),
            semantic: (record(tokens?.['semantic']) ?? {}),
            ...(record(tokens?.['component'])
                ? { component: record(tokens?.['component']) }
                : {}),
        },
        variants: normalizeVariants(variants),
    };
    if (meta?.['archetype'])
        manifest.archetype = meta['archetype'];
    if (meta?.['label'])
        manifest.label = meta['label'];
    if (meta?.['description'])
        manifest.description = meta['description'];
    if (pricingMonthly) {
        manifest.pricing = {
            monthlyCents: pricingMonthly,
            ...(pricingAnnual ? { annualCents: pricingAnnual } : {}),
        };
    }
    /**
     * 预览图要跟着一起带出来。
     *
     * 不保留的话，会出现一个很难查的症状：**重新发布一次（没换图）之后，
     * 市场里的图全没了** —— 因为前端把 manifest 重新合成了一遍，而合成丢了
     * `preview`，服务端收到的是一份"没有图"的 manifest，upsert 就把旧地址覆盖成空。
     * 发布页本身也靠这段把上次的图显示出来，让人不必每改一个字就重传两张图。
     */
    const previewNode = record(meta?.['preview']);
    if (previewNode) {
        const card = typeof previewNode['card'] === 'string' ? previewNode['card'] : undefined;
        const hero = typeof previewNode['hero'] === 'string' ? previewNode['hero'] : undefined;
        if (card || hero)
            manifest.preview = { ...(card ? { card } : {}), ...(hero ? { hero } : {}) };
    }
    /**
     * 模板层与组件样式同样必须原样带出来。
     *
     * 漏了 layout 的后果比漏 preview 更严重：manifest 里没有 layout，
     * 站点会判定"这套主题不接管版式"，于是回落到硬编码页头 + 客户数据库里的
     * 区块列表 —— 客户看到的是一套只有配色不同的主题，而市场缩略图上
     * 明明是一整套版式。这类"卖的和给的不一样"最难自证。
     */
    const layoutNode = record(input.layout) ?? record(meta?.['layout']);
    if (layoutNode && record(layoutNode['pages'])) {
        manifest.layout = layoutNode;
    }
    const stylesNode = record(meta?.['componentStyles']) ?? record((record(input.layout) ?? {})['componentStyles']);
    if (stylesNode) {
        manifest.componentStyles = stylesNode;
    }
    /**
     * 主题声明（`supports`）也要带出来 —— 这是 WordPress `add_theme_support()` 的对应物。
     *
     * 漏了不会报错，只会**静默退回** `['primary']`：页头照样渲染，但主题声明的
     * `footer` / `legal` 这些位置在控制台里看不到，客户想配页脚菜单也找不到入口。
     * 属于"不报错、但功能少一半"的那类丢失，所以合成时必须显式带上。
     */
    const supportsNode = record(meta?.['supports']);
    if (supportsNode) {
        manifest.supports = supportsNode;
    }
    return manifest;
}
/**
 * 从一堆上传的文件里认出主题包。
 *
 * 认的是**内容**不是文件名：员工可能把 theme.json 改名成 `forge.json`，
 * 按文件名判断会得到一句"缺少 theme.json"，而他手里明明三份都在。
 * 判据很简单 —— 有 `primitive`/`semantic` 的是令牌，有 `slug` 的是元信息，
 * 其余对象就是变体表。
 */
function readThemePackage(files) {
    const input = {};
    for (const file of files) {
        const body = record(file.json);
        if (!body)
            continue;
        const tokensNode = record(body['tokens']);
        if (tokensNode) {
            // 已经是一份完整 manifest
            return { meta: body, tokens: body['tokens'], variants: body['variants'] };
        }
        if (body['primitive'] || body['semantic']) {
            input.tokens = body;
            continue;
        }
        if (body['slug'] || body['label']) {
            input.meta = body;
            continue;
        }
        // 模板：有 version 与 pages，且页面值是对象。变体表的值是 { label, tokens }，不会撞
        if (body['pages'] && typeof body['pages'] === 'object') {
            input.layout = body;
            continue;
        }
        // 剩下的当变体表：值是 { label, tokens }
        const first = Object.values(body)[0];
        if (record(first) && ('label' in (record(first) ?? {}) || 'tokens' in (record(first) ?? {}))) {
            input.variants = body;
        }
    }
    return input;
}
//# sourceMappingURL=package.js.map